/**
 * SpendBlock — оболочка денежного блока: заголовок, список, хвост.
 *
 * Знает про раскладку и ничего не знает про смысл: и «Деньги под риском»,
 * и «Расход без обращений» устроены одинаково — крупная сумма из
 * знаменателя, список кампаний, строка про остаток. Отличаются они
 * содержимым строк, поэтому строки приходят детьми.
 *
 * Здесь же живут строковые примитивы обеих секций (`SpendRowAmount`,
 * `SpendRowName`, `SpendRowMeta`) — иначе задачи 4 и 5 объявили бы одни и
 * те же styled-компоненты дважды слово в слово.
 *
 * @format
 */

import type {ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {spacingPx} from 'ui/components/layout/spacing';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {useReportFormat} from '../format';
import {Disclosure} from './Disclosure';
import {CAMPAIGNS, MEASURE_WIDE, TABULAR, pluralize} from '../helpers';

/** Неразрывный пробел: держит предлог/союз при следующем слове (typography.md §2, §4). */
const NBSP = '\u00A0';

/** Одна кампания хвоста: имя и её доля расхода. */
export interface SpendBlockRestRow {
  key: string;
  name: string;
  spend: number;
}

/**
 * Хвост списка: сколько денег и в скольких кампаниях осталось.
 *
 * Строки нужны целиком, а не только их счёт: сумма без состава — это
 * «где-то ещё сто тысяч», и проверить её нечем.
 */
export interface SpendBlockRest {
  sum: number;
  count: number;
  rows: SpendBlockRestRow[];
}

export interface SpendBlockProps {
  /** Сумма блока — крупное число заголовка. */
  amount: number;
  /** Знаменатель: расход, от которого считается доля. */
  of: number;
  /** Доля в процентах. Приходит готовой из API, не пересчитывается. */
  share?: number | null;
  /** За какой срок посчитано: «при текущих настройках, за 30 дней». */
  caption: string;
  rest?: SpendBlockRest | null;
  children: ReactNode;
}

const Amount = styled(Box)(({theme}) => ({
  ...text('heading.xl'),
  ...TABULAR,
  color: theme.palette.bad.main,
}));

const Of = styled(Box)({
  ...text('heading.m'),
  ...TABULAR,
  color: surfaceVar('onSurfaceLight'),
});

const Caption = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE_WIDE,
});



/**
 * Ширина колонки сумм одинакова у всех строк обеих секций и берётся у
 * самой длинной: так цифры просматриваются столбиком, а не рваным краем.
 */
const AMOUNT_COLUMN = 210;

/** Сумма строки: колонка фиксированной ширины, на узком экране — во всю строку. */
export const SpendRowAmount = styled(Box)(({theme}) => ({
  ...text('heading.m'),
  ...TABULAR,
  color: theme.palette.bad.main,
  flex: `0 0 ${AMOUNT_COLUMN}px`,
  '@media (max-width: 640px)': {
    flex: '1 1 auto',
  },
}));

/** Название кампании. Длинные имена ломаются по символу, а не рвут раскладку. */
export const SpendRowName = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
  wordBreak: 'break-word',
});

/** Подпись под названием: счётчик срабатываний или клики. */
export const SpendRowMeta = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const RestTable = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RestRow = styled(Row)({
  paddingTop: spacingPx('xs'),
  paddingBottom: spacingPx('xs'),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${surfaceVar('surface2')}`,
  },
});

const RestName = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  minWidth: 0,
  overflowWrap: 'anywhere',
});

const RestSpend = styled(Box)({
  ...text('body.m'),
  ...TABULAR,
  color: surfaceVar('onSurfaceLight'),
  flex: 'none',
  whiteSpace: 'nowrap',
});

export function SpendBlock({amount, of, share, caption, rest, children}: SpendBlockProps) {
  const fmt = useReportFormat();
  const hasRest = rest != null && rest.count > 0;
  // Знаменатель нулевой или отрицательный — делить на него нечего: «из 0 ₽
  // — 46%» врёт о том, что доля посчитана. Хвост скрывается целиком, сумма
  // остаётся.
  const hasOf = of > 0;

  // «из» — предлог, липнет к следующему слову (typography.md §2). Доля идёт
  // через тире: перед ним NBSP (§4), а поскольку «46 %» — последнее слово
  // фразы длиннее двух слов, второй NBSP ставится и после тире (§1) —
  // тире целиком неразрывно с обеих сторон.
  //
  // Округление до целого прячет любую долю меньше половины процента за
  // «0%» — в денежном блоке это читается как «нисколько», хотя доля есть,
  // просто небольшая. `<1%` честнее нуля.
  const sharePercent = (value: number): string =>
    value > 0 && value < 0.5 ? '<1%' : `${Math.round(value)}%`;
  const ofShare = share == null ? '' : `${NBSP}—${NBSP}${sharePercent(share)}`;
  const ofText = `из${NBSP}${fmt.money(of)}${ofShare}`;

  return (
    <Stack gap='l'>
      <Stack gap='xxs'>
        <Row gap='s' alignY='baseline' wrap='wrap'>
          <Amount>{fmt.money(amount)}</Amount>
          {hasOf && <Of>{ofText}</Of>}
        </Row>
        <Caption>{caption}</Caption>
      </Stack>

      <Stack gap='s'>{children}</Stack>

      {/* «и» — союз, липнет к «ещё» (§2). `pluralize` даёт родительный
          падеж — «145 кампаний», — предлог «в» с ним не сочетается, поэтому
          сумма и счёт разделены тире, а не «в». Перед тире NBSP (§4);
          «145 кампаний» — последние два слова фразы длиннее двух слов,
          поэтому между числом и словом NBSP тоже (§1) — pluralize отдаёт
          обычный пробел, и здесь он заменяется. */}
      {hasRest && (
        <Disclosure
          label={`и${NBSP}ещё ${fmt.money(rest.sum)}${NBSP}— ${pluralize(rest.count, CAMPAIGNS).replace(' ', NBSP)}`}
          labelOpen='Свернуть'
          variant='outline'
        >
          {/* Таблица без оформления: два столбца, выключка по краям и
              волосяная линия между строками. Больше здесь нечему быть —
              это справка «куда делись остальные деньги», а не раздел. */}
          <RestTable>
            {rest.rows.map(row => (
              <RestRow key={row.key} gap='s' justify='between' alignY='baseline'>
                <RestName>{row.name}</RestName>
                <RestSpend>{fmt.money(row.spend)}</RestSpend>
              </RestRow>
            ))}
          </RestTable>
        </Disclosure>
      )}
    </Stack>
  );
}
